<script setup>
import { computed, useSlots } from 'vue'

const props = defineProps({
  audioSource: {
    type: [Object, String],
    default: null,
  },
  audioUrl: {
    type: String,
    default: '',
  },
  audioError: {
    type: String,
    default: '',
  },
  isAudioLoading: {
    type: Boolean,
    default: false,
  },
  audioLabel: {
    type: String,
    default: 'AI response audio',
  },
})

const slots = useSlots()

const playerSource = computed(() => props.audioSource || props.audioUrl)
const hasCustomAudioSlot = computed(() => Boolean(slots.default))
const hasAudioControls = computed(
  () =>
    hasCustomAudioSlot.value ||
    props.isAudioLoading ||
    Boolean(props.audioUrl) ||
    Boolean(props.audioError),
)
</script>

<template>
  <div v-if="hasAudioControls" class="audio-controls">
    <slot
      :audio-source="audioSource"
      :audio-url="audioUrl"
      :error="audioError"
      :is-loading="isAudioLoading"
    >
      <span v-if="isAudioLoading" class="audio-status">Audio...</span>
      <media-player
        v-if="audioUrl"
        class="speech-player"
        :src="playerSource"
        view-type="audio"
        stream-type="on-demand"
        load="eager"
        preload="auto"
        :aria-label="audioLabel"
        autoplay
      >
        <media-provider></media-provider>
        <media-controls class="speech-player-controls vds-controls">
          <media-controls-group class="speech-player-row vds-controls-group">
            <media-play-button class="speech-player-button vds-button">
              <span class="speech-player-icon speech-player-icon-play" aria-hidden="true"></span>
              <span class="speech-player-icon speech-player-icon-pause" aria-hidden="true"></span>
            </media-play-button>

            <media-time-slider class="speech-player-slider vds-slider" aria-label="Audio progress">
              <div class="vds-slider-track"></div>
              <div class="vds-slider-track-progress"></div>
              <div class="vds-slider-track-fill"></div>
              <div class="vds-slider-thumb"></div>
            </media-time-slider>

            <div class="speech-player-time">
              <media-time class="speech-player-time-value vds-time" type="current"></media-time>
              <span class="speech-player-time-separator" aria-hidden="true">/</span>
              <media-time class="speech-player-time-value vds-time" type="duration"></media-time>
            </div>
          </media-controls-group>
        </media-controls>
      </media-player>
      <span v-if="audioError" class="audio-error">{{ audioError }}</span>
    </slot>
  </div>
</template>

<style scoped>
.audio-controls {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-top: 0.55rem;
}

.audio-status,
.audio-error {
  font-size: 0.78rem;
  font-weight: 800;
}

.audio-status {
  color: #4b554a;
}

.audio-error {
  color: #8b1e1e;
}

.speech-player {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  border-radius: 6px;
  background: #ffffff;
  color: #17301c;
  border: 1px solid rgba(76, 175, 80, 0.22);
  --media-brand: #4caf50;
  --media-focus-ring-color: rgba(76, 175, 80, 0.45);
  --media-focus-ring: 0 0 0 3px var(--media-focus-ring-color);
  --media-button-size: 1.9rem;
  --media-button-icon-size: 58%;
  --media-slider-height: 1.9rem;
  --media-slider-track-height: 4px;
  --media-slider-thumb-size: 0.72rem;
  --media-slider-track-bg: rgba(23, 48, 28, 0.18);
  --media-slider-track-fill-bg: #4caf50;
  --media-slider-track-progress-bg: rgba(23, 48, 28, 0.28);
  --media-slider-thumb-bg: #17301c;
  --media-time-font-size: 0.72rem;
  --media-time-color: #425143;
  --media-time-padding: 0;
}

.speech-player-controls {
  display: block;
  width: 100%;
}

.speech-player-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  min-width: 0;
  padding: 0.38rem 0.55rem;
}

.speech-player-button {
  color: #17301c;
  background: rgba(76, 175, 80, 0.12);
  border-radius: 999px;
}

.speech-player-button:hover {
  background: rgba(76, 175, 80, 0.2);
}

.speech-player-button[data-ended] {
  background: #4caf50;
  color: #08120a;
}

.speech-player-button:not([data-paused]) .speech-player-icon-play,
.speech-player-button[data-paused] .speech-player-icon-pause,
.speech-player-button[data-ended] .speech-player-icon-pause {
  display: none;
}

.speech-player-button[data-ended] .speech-player-icon-play {
  display: block;
}

.speech-player-icon {
  width: 0.85rem;
  height: 0.85rem;
}

.speech-player-icon-play {
  width: 0;
  height: 0;
  margin-left: 0.12rem;
  border-top: 0.42rem solid transparent;
  border-bottom: 0.42rem solid transparent;
  border-left: 0.62rem solid currentColor;
}

.speech-player-icon-pause {
  position: relative;
}

.speech-player-icon-pause::before,
.speech-player-icon-pause::after {
  content: '';
  position: absolute;
  top: 0.06rem;
  bottom: 0.06rem;
  width: 0.24rem;
  border-radius: 999px;
  background: currentColor;
}

.speech-player-icon-pause::before {
  left: 0.14rem;
}

.speech-player-icon-pause::after {
  right: 0.14rem;
}

.speech-player-slider {
  width: 100%;
  min-width: 0;
  margin: 0;
}

.speech-player-time {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  min-width: 4.5rem;
  justify-content: flex-end;
  color: #425143;
  font-variant-numeric: tabular-nums;
}

.speech-player-time-value {
  min-width: 1.7rem;
  text-align: center;
}

.speech-player-time-separator {
  color: rgba(66, 81, 67, 0.62);
}

@media (max-width: 640px) {
  .speech-player-row {
    gap: 0.45rem;
    padding-inline: 0.45rem;
  }

  .speech-player-time {
    display: none;
  }
}
</style>
